// Hand-written client for the EntityTemplate "create from template" endpoints.
// These custom endpoints live on the manual controller (EntityTemplateController.cs) and
// are NOT part of the generated CRUD service (EntityTemplateService.ts). Keep this file
// generic — it is driven by entity name, never per-entity.

import { apiClient, getErrorMessage } from '@/lib/apiClient';
import type {
  CreateEntityTemplate,
  EntityTemplate,
  UpdateEntityTemplate,
} from '@/types/core/EntityTemplate';

const baseUrl = '/api/core/entitytemplate';

/** Lightweight template projection returned by the picker (no payload). */
export interface EntityTemplateListItem {
  id: string;
  entityName: string;
  name: string;
  description?: string;
  visibility: string;
  isDefault: boolean;
  ownerUserId?: string;
  orgUnitId?: string;
  creationTime: string;
}

/** The validated, restriction-stripped starting values for a new record. */
export interface ResolvedTemplatePayload {
  templateId: string;
  entityName: string;
  /** Partial Create<Entity>Dto (camelCase) to merge into the create form's initial state. */
  payloadJson?: string;
}

/** Templates for an entity, scope-filtered to the current caller (default first). */
export async function getTemplatesForEntity(entityName: string): Promise<EntityTemplateListItem[]> {
  try {
    const response = await apiClient.get<EntityTemplateListItem[]>(
      `${baseUrl}/for-entity/${encodeURIComponent(entityName)}`,
    );
    return response.data ?? [];
  } catch (error) {
    console.error('Error fetching templates for entity:', error);
    throw new Error(getErrorMessage(error));
  }
}

/** Save current form values as a reusable template ("Save as template"). */
export async function saveTemplate(input: CreateEntityTemplate): Promise<EntityTemplate> {
  try {
    const response = await apiClient.post<EntityTemplate>(`${baseUrl}/save`, input);
    return response.data;
  } catch (error) {
    console.error('Error saving template:', error);
    throw new Error(getErrorMessage(error));
  }
}

/** Resolve a template's payload as an object ready to seed the create form. */
export async function resolveTemplatePayload(id: string): Promise<Record<string, unknown>> {
  try {
    const response = await apiClient.get<ResolvedTemplatePayload>(`${baseUrl}/${id}/resolve-payload`);
    const payloadJson = response.data?.payloadJson;
    if (!payloadJson) return {};
    try {
      return JSON.parse(payloadJson) as Record<string, unknown>;
    } catch {
      console.warn('Template payload was not valid JSON; seeding blank form.');
      return {};
    }
  } catch (error) {
    console.error('Error resolving template payload:', error);
    throw new Error(getErrorMessage(error));
  }
}

/** Mark a template the scope default (clears the prior default at the same scope). */
export async function setTemplateDefault(id: string): Promise<void> {
  try {
    await apiClient.post(`${baseUrl}/${id}/set-default`);
  } catch (error) {
    console.error('Error setting default template:', error);
    throw new Error(getErrorMessage(error));
  }
}

/** Clear a template's default flag (inverse of {@link setTemplateDefault}). */
export async function clearTemplateDefault(id: string): Promise<void> {
  try {
    await apiClient.post(`${baseUrl}/${id}/clear-default`);
  } catch (error) {
    console.error('Error clearing default template:', error);
    throw new Error(getErrorMessage(error));
  }
}

/**
 * Fetch a template by id with its RAW stored payload — used to seed the form when
 * EDITING a template. Unlike {@link resolveTemplatePayload} (which sanitizes the payload
 * for *applying* to a new record), this returns exactly what was saved so the editor
 * round-trips faithfully. Also yields name/description/visibility for the save dialog.
 */
export async function getTemplateById(id: string): Promise<EntityTemplate> {
  try {
    const response = await apiClient.get<EntityTemplate>(`${baseUrl}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching template:', error);
    throw new Error(getErrorMessage(error));
  }
}

/** Update an existing template's values (name/visibility/payload) via the validated /save path. */
export async function updateTemplate(id: string, input: UpdateEntityTemplate): Promise<EntityTemplate> {
  try {
    const response = await apiClient.put<EntityTemplate>(`${baseUrl}/${id}/save`, input);
    return response.data;
  } catch (error) {
    console.error('Error updating template:', error);
    throw new Error(getErrorMessage(error));
  }
}

/** Permanently delete a template. */
export async function deleteTemplate(id: string): Promise<void> {
  try {
    await apiClient.delete(`${baseUrl}/${id}`);
  } catch (error) {
    console.error('Error deleting template:', error);
    throw new Error(getErrorMessage(error));
  }
}
